<template>
  <div class="input__wrapper">
    <div class="input__info__tooltip-container">
      <Tooltip v-if="isTooltip" :tooltip-options="tooltipOptions">
        <template #parent>
          <div class="input__info__tooltip-container__target" />
        </template>
        <slot name="tooltip" />
      </Tooltip>
    </div>
    <div class="input__info">
      <div class="input__left">
        <div ref="inputLabel" class="input__label">
          {{ label }}
          <span v-if="required" class="input__required"> * </span>
        </div>
        <div
          v-if="tooltipOptions.open !== undefined"
          class="input__prompt"
          @mouseenter="$emit('mouseenter', $event)"
          @mouseleave="$emit('mouseleave', $event)"
        >
          <PromptIcon />
        </div>
      </div>
      <div class="input__error" :class="{ input__error_active: error }">
        {{ errorMessage }}
      </div>
    </div>
    <component
      :is="currentType"
      class="input"
      :placeholder="placeholder"
      :value="value"
      :max-length="maxLength"
      :class="{ input_error: error }"
      :new-password="newPassword"
      :disabled="disabled"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
      @changedValue="$emit('input', $event)"
    >
      <template #leftIconChild>
        <slot name="leftIcon" />
      </template>
      <template #rightIconChild>
        <slot name="rightIcon" />
      </template>
    </component>
  </div>
</template>

<script>
import PhoneInput from './PhoneInput.vue';
import EmailInput from './EmailInput.vue';
import PasswordInput from './PasswordInput.vue';
import TextInput from './TextInput.vue';
import CityInput from './CityInput.vue';
import TextareaInput from './TextareaInput.vue';
import PromptIcon from '@/components/Icons/PromptIcon.vue';
import Tooltip from '@/components/UiKit/v2/Tooltip/index.vue';

export default {
  name: 'Input',
  components: {
    PhoneInput,
    EmailInput,
    PasswordInput,
    TextInput,
    CityInput,
    TextareaInput,
    PromptIcon,
    Tooltip
  },
  props: {
    type: {
      required: true,
      type: String,
      validator: function (value) {
        return ['phone', 'textarea', 'email', 'password', 'text', 'city'].indexOf(value) !== -1;
      }
    },
    isTooltip: {
      type: Boolean,
      default: false
    },
    tooltipOptions: {
      type: Object,
      default: () => ({})
    },
    placeholder: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number
    },
    label: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      required: true
    },
    errorMessage: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    newPassword: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentType() {
      return `${this.type}-input`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

input::placeholder {
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.3px;
  color: #76767b;
}

.input {
  $input-height: 64px;

  width: 100%;
  border: 1px solid #f7f7fb;
  border-radius: 4px;
  background: #f7f7fb;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.3px;
  color: #36383e;
  box-sizing: border-box;
  transition: 0.3s;

  @include uiFont;
  @extend %text-400-14;

  &_error {
    border: 1px solid #eb5757;
  }

  &__left-icon {
    display: flex;
    width: fit-content;
  }

  &__wrapper {
    position: relative;
    width: 100%;
    @include uiFont;
  }

  &__info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;

    &__tooltip-container {
      position: absolute;
      width: 100%;
      height: $input-height;
      pointer-events: none;

      &__target {
        width: 100%;
        height: $input-height;
      }
    }
  }

  &__label {
    color: #ababba;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.3px;
    margin-right: 8px;
  }

  &__required {
    color: #eb5757;
  }

  &__left {
    display: flex;
    align-items: center;
  }

  &__prompt {
    position: relative;
    display: flex;
    cursor: pointer;
  }

  &__error {
    font-weight: 500;
    font-size: 12px;
    letter-spacing: -0.3px;
    color: #eb5757;
    opacity: 0;
    transition: 0.3s;

    &_active {
      opacity: 1;
    }
  }
}
</style>
